import { useSlideScreenshotQueue } from "@/components/slides/screenshot/SlideScreenshotQueue";
import { isFreeformCanvas } from "@/lib/constants/slides";
import type { SlideWithSubslides } from "@/lib/hooks/slides/useSlidesByLessonId";
import { useEffect } from "react";

/**
 * Shoots thumbnails for the lesson's canvas slides that have none.
 *
 * A canvas thumbnail is a browser capture, so a generation run whose tab was closed, reloaded or
 * interrupted leaves its slides blank with nothing server-side able to fix it. Listing the lesson
 * is the next moment a browser is in a position to take the picture, so it takes it. The queue
 * allows each slide one attempt per session, so a slide that cannot be captured is not re-shot on
 * every visit.
 */
export function useHealMissingThumbnails(slides: SlideWithSubslides[]) {
  const enqueue = useSlideScreenshotQueue();

  const unshot = slides
    .flatMap((slide) => [slide, ...slide.children])
    .filter((slide) => !slide.imageId && isFreeformCanvas(slide.type, slide.knowledgeArea))
    .map((slide) => slide.id);

  // Joined: the list is rebuilt every render as pages land, but the ids are what actually changed.
  const unshotKey = unshot.join(",");

  useEffect(() => {
    if (unshotKey === "") return;
    enqueue(unshotKey.split(","));
  }, [unshotKey, enqueue]);
}
